import React from 'react';
import {
  FunnelIcon,
  Cog6ToothIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/24/outline';
import { FailureFilters } from '@/types/filters';
import { ForecastConfig, SimulationMetadata } from '@/types/forecast';
import { Workspace } from '@/types/workspace';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { useLayout } from '@/contexts';
import { ForecastFiltersSidebar } from './ForecastFiltersSidebar';
import { ForecastConfigurationSidebar } from './ForecastConfigurationSidebar';

interface ForecastSidebarControllerProps {
  // Filter props
  filters: FailureFilters;
  onFiltersChange: (newFilters: FailureFilters) => void;
  sourceWorkspace: Workspace | null;

  // Configuration props
  configuration: ForecastConfig;
  onConfigurationChange: (config: ForecastConfig) => void;
  populationSize: number;
  forecastSubsetSize?: number;

  // Parameters props
  sourceAnalysis: WeibullAnalysisResult | null;
  simulationMetadata: SimulationMetadata | null;

  // Common props
  disabled?: boolean;
}

export const ForecastSidebarController: React.FC<ForecastSidebarControllerProps> = ({
  // Filter props
  filters,
  onFiltersChange,
  sourceWorkspace,

  // Configuration props
  configuration,
  onConfigurationChange,
  populationSize,
  forecastSubsetSize,

  // Parameters props
  sourceAnalysis,
  simulationMetadata,

  // Common props
  disabled = false,
}) => {
  const { rightSidebarType, setRightSidebarType } = useLayout();

  // Toggle individual sidebars
  const toggleSidebar = (type: 'filters' | 'analysis') => {
    const current = rightSidebarType;
    if (current === 'none') {
      setRightSidebarType(type);
    } else if (current === type) {
      setRightSidebarType('none');
    } else if (current === 'both') {
      setRightSidebarType(type === 'filters' ? 'analysis' : 'filters');
    } else {
      setRightSidebarType('both');
    }
  };

  // Toggle all sidebars
  const toggleAll = () => {
    const current = rightSidebarType;
    if (current === 'both') {
      setRightSidebarType('none');
    } else {
      setRightSidebarType('both');
    }
  };

  // Determine if specific sidebar is active
  const isFilterSidebarActive = rightSidebarType === 'filters' || rightSidebarType === 'both';
  const isConfigSidebarActive = rightSidebarType === 'analysis' || rightSidebarType === 'both';

  return (
    <div className="flex h-full">
      {/* Vertically Stacked Sidebars */}
      <div className="flex flex-col h-full">
        {/* Filter Sidebar */}
        {isFilterSidebarActive && (
          <div className={`${isConfigSidebarActive ? 'h-1/2' : 'h-full'}`}>
            <ForecastFiltersSidebar
              filters={filters}
              onFiltersChange={onFiltersChange}
              sourceWorkspace={sourceWorkspace}
              disabled={disabled}
              isCollapsed={false}
              onToggleCollapse={() => toggleSidebar('filters')}
            />
          </div>
        )}

        {/* Configuration & Parameters Sidebar */}
        {isConfigSidebarActive && (
          <div className={isFilterSidebarActive ? "h-1/2" : "h-full"}>
            <ForecastConfigurationSidebar
              configuration={configuration}
              onConfigurationChange={onConfigurationChange}
              populationSize={populationSize}
              forecastSubsetSize={forecastSubsetSize}
              sourceAnalysis={sourceAnalysis}
              simulationMetadata={simulationMetadata}
              disabled={disabled}
              isCollapsed={false}
              onToggleCollapse={() => toggleSidebar('analysis')}
            />
          </div>
        )}
      </div>

      {/* Icon Strip - Always visible on the right */}
      <div className="bg-white h-full flex flex-col w-12 relative border-l border-gray-200">
        {/* Main Toggle Button */}
        <div className="p-2">
          <button
            onClick={toggleAll}
            className="p-2 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title={rightSidebarType === 'both' ? 'Close all sidebars' : 'Open all sidebars'}
          >
            {rightSidebarType === 'both' ? (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Individual Sidebar Icons - Always visible with active state */}
        <div className="flex flex-col space-y-2 p-2">
          {/* Filter Icon */}
          <button
            onClick={() => toggleSidebar('filters')}
            className={`p-2 rounded-md transition-colors relative ${
              isFilterSidebarActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
            }`}
            title={isFilterSidebarActive ? 'Close filters' : 'Open filters'}
          >
            <FunnelIcon className="h-5 w-5" />
            {(() => {
              const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
                if (key === 'excludedCaseIds' && Array.isArray(value) && value.length > 0) {
                  return count + 1; // Count excluded cases as one filter
                } else if (Array.isArray(value)) {
                  return count + value.length;
                } else if (value && key !== 'page' && key !== 'size') {
                  return count + 1;
                }
                return count;
              }, 0);

              return activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              );
            })()}
          </button>

          {/* Configuration Icon */}
          <button
            onClick={() => toggleSidebar('analysis')}
            className={`p-2 rounded-md transition-colors ${
              isConfigSidebarActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
            }`}
            title={isConfigSidebarActive ? 'Close configuration' : 'Open configuration'}
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForecastSidebarController;